import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, FileText, Link as LinkIcon } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';

interface ExtractionErrorMessageProps {
  errorType?: string;
  errorMessage: string;
  attemptCount?: number;
  documentType: string;
  onReupload?: () => void;
  onViewRequirements?: () => void;
}

const ExtractionErrorMessage: React.FC<ExtractionErrorMessageProps> = ({
  errorType,
  errorMessage,
  attemptCount = 1,
  documentType,
  onReupload,
  onViewRequirements
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getErrorDetails = () => {
    switch (errorType) {
      case 'insufficient_content':
        return {
          title: 'Image-Based or Scanned PDF Detected',
          summary: 'Your PDF appears to be image-based or scanned, which prevents text extraction.',
          steps: [
            'Export your resume from the original application (Word, Google Docs, etc.) as a PDF',
            'Ensure "Preserve text" or similar option is enabled when exporting',
            'Open the PDF and try to select text with your cursor - if you can\'t select text, it\'s image-based',
            'If you only have a scanned copy, try using an online OCR service first, then export the result as PDF'
          ],
          resources: [
            { label: 'How to Export Text-Based PDFs', link: '/user-guide#pdf-requirements' },
            { label: 'PDF Requirements Guide', link: '#' }
          ]
        };

      case 'file_not_found':
        return {
          title: 'File Not Found in Storage',
          summary: 'The uploaded file could not be found in our storage system.',
          steps: [
            'Try uploading your document again',
            'Ensure you have a stable internet connection',
            'If the problem persists, try using a different browser',
            'Contact support if the issue continues'
          ],
          resources: [
            { label: 'Contact Support', link: '/support' }
          ]
        };

      case 'download_failed':
        return {
          title: 'Download Failed',
          summary: 'We couldn\'t download your file from storage for processing.',
          steps: [
            'Check your internet connection and try again',
            'The file may be too large - ensure it\'s under 10MB',
            'Try re-uploading the document',
            'If using a VPN, try disabling it temporarily'
          ],
          resources: []
        };

      case 'timeout':
        return {
          title: 'Processing Timeout',
          summary: 'Text extraction took too long and was stopped to prevent system issues.',
          steps: [
            'Your PDF may be very large or complex - try reducing file size',
            'Remove unnecessary images or graphics from your resume',
            'Simplify the layout to a single-column format',
            'Try uploading again during off-peak hours'
          ],
          resources: [
            { label: 'How to Optimize PDFs', link: '/user-guide#optimize-pdf' }
          ]
        };

      case 'password_protected':
      case 'encrypted':
        return {
          title: 'Password-Protected PDF',
          summary: 'Your PDF is password-protected or encrypted, which prevents text extraction.',
          steps: [
            'Open the PDF in Adobe Acrobat or Preview',
            'Print to PDF (this removes password protection)',
            'Or use "Save As" and ensure no security is applied',
            'Upload the unprotected version'
          ],
          resources: [
            { label: 'How to Remove PDF Passwords', link: '/user-guide#remove-password' }
          ]
        };

      case 'corrupted':
      case 'invalid':
        return {
          title: 'Corrupted or Invalid PDF',
          summary: 'The PDF file appears to be corrupted or in an invalid format.',
          steps: [
            'Try opening the PDF - if it won\'t open, it\'s corrupted',
            'Re-export from the original source (Word, Google Docs, etc.)',
            'Try "Save As" in a PDF viewer to create a clean copy',
            'Ensure the file has a .pdf extension',
            'Check that the file size is reasonable (not 0 bytes or extremely large)'
          ],
          resources: [
            { label: 'PDF Requirements', link: '#' }
          ]
        };

      case 'extraction_failed':
      default:
        return {
          title: 'Text Extraction Failed',
          summary: 'We couldn\'t extract readable text from your PDF.',
          steps: [
            'Ensure your PDF contains selectable text (not images)',
            'Try exporting from the original source application',
            'Check that the PDF isn\'t password-protected',
            'Verify the file isn\'t corrupted by opening it',
            'Try a different PDF export method or format'
          ],
          resources: [
            { label: 'PDF Requirements Guide', link: '#' },
            { label: 'Common Solutions', link: '/user-guide#extraction-issues' }
          ]
        };
    }
  };

  const details = getErrorDetails();

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="font-semibold text-red-900 mb-1">
                {details.title}
              </h4>
              {attemptCount > 1 && (
                <p className="text-xs text-red-700">
                  Failed after {attemptCount} {attemptCount === 1 ? 'attempt' : 'attempts'}
                </p>
              )}
            </div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-red-700 hover:text-red-900 p-1"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Summary */}
          <p className="text-sm text-red-900 mb-3">
            {details.summary}
          </p>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="space-y-3">
              {/* Remediation Steps */}
              <div>
                <p className="text-xs font-semibold text-red-900 mb-2">
                  How to Fix This:
                </p>
                <ol className="text-xs text-red-800 space-y-1.5 list-decimal list-inside">
                  {details.steps.map((step, idx) => (
                    <li key={idx} className="leading-relaxed">{step}</li>
                  ))}
                </ol>
              </div>

              {/* Resources */}
              {details.resources.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-900 mb-2">
                    Helpful Resources:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {details.resources.map((resource, idx) => (
                      <a
                        key={idx}
                        href={resource.link}
                        onClick={(e) => {
                          if (resource.link === '#' && onViewRequirements) {
                            e.preventDefault();
                            onViewRequirements();
                          }
                        }}
                        className="inline-flex items-center gap-1 text-xs text-red-700 hover:text-red-900 underline"
                      >
                        <LinkIcon className="h-3 w-3" />
                        {resource.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Technical Details */}
              <div className="pt-2 border-t border-red-200">
                <p className="text-xs font-semibold text-red-900 mb-1">
                  Technical Details:
                </p>
                <p className="text-xs text-red-700 font-mono bg-red-100 p-2 rounded break-words">
                  {errorMessage}
                </p>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mt-3">
            {onReupload && (
              <Button
                size="sm"
                onClick={onReupload}
                className="bg-white border border-red-300 text-red-700 hover:bg-red-50"
              >
                <FileText className="h-3 w-3 mr-1" />
                Upload Better PDF
              </Button>
            )}
            {onViewRequirements && (
              <Button
                size="sm"
                variant="outline"
                onClick={onViewRequirements}
                className="border-red-200 text-red-700 hover:bg-red-50"
              >
                View PDF Requirements
              </Button>
            )}
            {!isExpanded && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsExpanded(true)}
                className="text-red-700 hover:bg-red-50"
              >
                Show How to Fix
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtractionErrorMessage;
